import { XCircle } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Utility functions for rendering remote peer profile preferences
 * These are event-driven and read from contact.custom.profile
 */

export interface RemotePeerProfile {
  avatarColor?: string;
  avatarEmoji?: string;
  bannerColor?: string;
  presence?: "online" | "invisible" | "dnd";
  lastUpdated?: number;
}

/**
 * Get avatar background color for a remote peer
 * Falls back to default blue-gray if no custom color is set
 */
export function getPeerAvatarColor(profile?: RemotePeerProfile): string {
  return profile?.avatarColor || "#546E7A"; // Default blue-gray
}

/**
 * Get banner background color for a remote peer
 * Falls back to theme-aware default if no custom color is set
 */
export function getPeerBannerColor(profile?: RemotePeerProfile): string {
  if (profile?.bannerColor) return profile.bannerColor;
  // Match useUserPreferences.ts defaults
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "#263238"   // Blue-gray-900 (dark mode)
    : "#B0BEC5";  // Blue-gray-300 (light mode)
}

/**
 * Render emoji overlay for peer avatar if present
 */
export function renderPeerAvatarEmoji(
  profile?: RemotePeerProfile,
  size: "sm" | "md" | "lg" = "md"
): ReactNode {
  if (!profile?.avatarEmoji) return null;

  const sizeMap = {
    sm: "text-sm",
    md: "text-lg",
    lg: "text-3xl"
  };

  const positionMap = {
    sm: { top: '-1px', right: '-1px' },
    md: { top: '-2px', right: '-2px' },
    lg: { top: '-4px', right: '-4px' }
  };

  return (
    <span 
      className={`absolute ${sizeMap[size]}`} 
      style={positionMap[size]}
    >
      {profile.avatarEmoji}
    </span>
  );
}

/**
 * Render presence badge for peer (online, DND, or nothing for invisible)
 */
export function renderPeerPresenceBadge(
  profile?: RemotePeerProfile,
  size: "sm" | "md" = "md"
): ReactNode {
  if (!profile?.presence || profile.presence === "invisible") {
    return null; // No badge for invisible
  }

  const sizeClass = size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3";
  const iconSize = size === "sm" ? 6 : 8;

  if (profile.presence === "online") {
    return (
      <div 
        className={`absolute -bottom-1 -right-1 bg-green-500 rounded-full ${sizeClass} border-2 border-surface`}
        title="Online"
      />
    );
  }

  if (profile.presence === "dnd") {
    return (
      <div 
        className={`absolute -bottom-1 -right-1 bg-black rounded-full ${sizeClass} border-2 border-surface flex items-center justify-center`}
        title="Do Not Disturb"
      >
        <XCircle size={iconSize} className="text-white" strokeWidth={3} />
      </div>
    );
  }

  return null;
}
